import { writable } from 'svelte/store';
import { browser } from '$app/environment';
import { playTimerSound, stopTimerSound } from '$lib/utils/sound';

export interface Timer {
    id: string;
    label: string;
    duration: number;
    endTime: number;
    isPaused: boolean;
    pausedRemainingTime?: number;
    isCompleted: boolean;
    notificationSent?: boolean;
}

interface TimersState {
    timers: Timer[];
    maxTimers: number;
}

const STORAGE_KEY = 'multi-countdown-timers';

// Simple save function
function saveToStorage(state: TimersState) {
    if (!browser) return;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        // console.log('✅ SAVED', state.timers.length, 'timers to localStorage');
    } catch (e) {
        console.error('❌ FAILED to save timers:', e);
    }
}

// Simple load function
function loadFromStorage(): TimersState {
    if (!browser) return { timers: [], maxTimers: 20 };
    
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        // console.log('📦 LOADING from localStorage:', saved ? 'Found data' : 'No data');
        
        if (!saved) {
            return { timers: [], maxTimers: 20 };
        }
        
        const parsed = JSON.parse(saved);
        // console.log('🔍 PARSED DATA:', parsed);
        
        if (!parsed || !Array.isArray(parsed.timers)) {
            console.warn('⚠️ Invalid data format, resetting');
            return { timers: [], maxTimers: 20 };
        }
        
        // console.log('🔍 RAW TIMERS:', parsed.timers);
        // console.log('🔍 NUMBER OF RAW TIMERS:', parsed.timers.length);
        
        const now = Date.now();
        const timers = parsed.timers
            .filter((t: any) => {
                const isValid = t && t.id && t.duration;
                if (!isValid) {
                    // console.log('❌ FILTERING OUT INVALID TIMER:', t);
                }
                return isValid;
            }) // Filter out invalid timers
            .map((timer: any) => {
                // console.log('✅ PROCESSING VALID TIMER:', timer);
                let endTime = timer.endTime || (now + (timer.remainingTime || 0));
                let isPaused = Boolean(timer.isPaused);
                let pausedRemainingTime = timer.pausedRemainingTime;
                let isCompleted = isPaused ? false : now >= endTime;
                
                const result = {
                    id: timer.id,
                    label: timer.label || '',
                    duration: timer.duration,
                    endTime,
                    isPaused,
                    pausedRemainingTime,
                    isCompleted
                };
                // console.log('✅ PROCESSED TIMER RESULT:', result);
                return result;
            });
        
        const result = { timers, maxTimers: parsed.maxTimers || 20 };
        // console.log('✅ LOADED', result.timers.length, 'timers successfully');
        return result;
    } catch (e) {
        console.error('❌ FAILED to load timers:', e);
        localStorage.removeItem(STORAGE_KEY);
        return { timers: [], maxTimers: 20 };
    }
}

function createTimersStore() {
    const initialState = loadFromStorage();
    const { subscribe, set, update } = writable<TimersState>(initialState);

    return {
        subscribe,
        addTimer: (duration: number, label: string = '') => {
            update(state => {
                if (state.timers.length >= state.maxTimers) {
                    throw new Error(`Maximum of ${state.maxTimers} timers reached`);
                }
                
                const now = Date.now();
                const newTimer: Timer = {
                    id: crypto.randomUUID(),
                    label,
                    duration,
                    endTime: now + duration,
                    isPaused: false,
                    isCompleted: false
                };
                
                const newState = {
                    ...state,
                    timers: [...state.timers, newTimer]
                };
                
                saveToStorage(newState); // Save immediately
                return newState;
            });
        },
        removeTimer: (id: string) => {
            update(state => {
                const newState = {
                    ...state,
                    timers: state.timers.filter(timer => timer.id !== id)
                };
                saveToStorage(newState); // Save immediately
                return newState;
            });
        },
        togglePause: (id: string) => {
            update(state => {
                const now = Date.now();
                const newState = {
                    ...state,
                    timers: state.timers.map(timer => {
                        if (timer.id === id) {
                            if (timer.isPaused) {
                                // Resuming
                                const remainingTime = timer.pausedRemainingTime || 0;
                                return {
                                    ...timer,
                                    isPaused: false,
                                    endTime: now + remainingTime,
                                    pausedRemainingTime: undefined
                                };
                            } else {
                                // Pausing
                                const remainingTime = Math.max(0, timer.endTime - now);
                                return {
                                    ...timer,
                                    isPaused: true,
                                    pausedRemainingTime: remainingTime
                                };
                            }
                        }
                        return timer;
                    })
                };
                saveToStorage(newState); // Save immediately
                return newState;
            });
        },
        resetTimer: (id: string) => {
            update(state => {
                const now = Date.now();
                const newState = {
                    ...state,
                    timers: state.timers.map(timer => {
                        if (timer.id === id) {
                            return {
                                ...timer,
                                endTime: now + timer.duration,
                                isPaused: false,
                                pausedRemainingTime: undefined,
                                isCompleted: false
                            };
                        }
                        return timer;
                    })
                };
                saveToStorage(newState); // Save immediately
                return newState;
            });
        },
        updateTimers: () => {
            update(state => {
                const now = Date.now();
                let hasCompletedTimer = false;
                
                const updatedTimers = state.timers.map(timer => {
                    if (timer.isPaused || timer.isCompleted) {
                        return timer;
                    }
                    
                    const isCompleted = now >= timer.endTime;
                    
                    if (isCompleted && !timer.isCompleted) {
                        hasCompletedTimer = true;
                    }
                    
                    return {
                        ...timer,
                        isCompleted
                    };
                });
                
                // Play sound if any timer completed
                if (hasCompletedTimer) {
                    playTimerSound(30000);
                }
                
                const newState = { 
                    timers: updatedTimers,
                    maxTimers: state.maxTimers
                };
                
                // Only save if something actually changed
                const hasChanges = updatedTimers.some((timer, index) => 
                    timer.isCompleted !== state.timers[index]?.isCompleted
                );
                if (hasChanges) {
                    saveToStorage(newState);
                }
                
                return newState;
            });
        },
        clearAll: () => {
            update(state => {
                const newState = {
                    ...state,
                    timers: []
                };
                saveToStorage(newState); // Save immediately
                stopTimerSound();
                return newState;
            });
        }
    };
}

export const timers = createTimersStore();

let backgroundCheckInterval: number | null = null;
let notificationCallbacks: ((timer: Timer) => void)[] = [];

export function registerNotificationCallback(callback: (timer: Timer) => void) {
    notificationCallbacks.push(callback);
    return () => {
        notificationCallbacks = notificationCallbacks.filter(cb => cb !== callback);
    };
}

function checkTimersInBackground() {
    if (!browser) return;

    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;

    try {
        const state: TimersState = JSON.parse(saved);
        const now = Date.now();
        let needsSave = false;

        const updatedTimers = state.timers.map(timer => {
            if (timer.isPaused || timer.isCompleted) {
                return timer;
            }

            const isCompleted = now >= timer.endTime;

            if (isCompleted && !timer.isCompleted && !timer.notificationSent) {
                notificationCallbacks.forEach(callback => callback(timer));
                needsSave = true;
                return {
                    ...timer,
                    isCompleted: true,
                    notificationSent: true
                };
            }

            if (isCompleted && !timer.isCompleted) {
                needsSave = true;
                return { ...timer, isCompleted: true };
            }

            return timer;
        });

        if (needsSave) {
            const newState = { ...state, timers: updatedTimers };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
        }
    } catch (e) {
        console.error('Background check error:', e);
    }

    const pomodoroSaved = localStorage.getItem('pomodoroTimerState');
    if (pomodoroSaved) {
        try {
            const pomodoroState = JSON.parse(pomodoroSaved);
            if (pomodoroState.isRunning && pomodoroState.endTime) {
                const currentTime = Date.now();
                if (currentTime >= pomodoroState.endTime && !pomodoroState.notificationSent) {
                    if ('Notification' in window && Notification.permission === 'granted') {
                        const message = pomodoroState.mode === 'pomodoro'
                            ? 'Time for a break!'
                            : pomodoroState.mode === 'shortBreak'
                                ? 'Short break is over! Back to work.'
                                : 'Long break is over! Ready to focus?';

                        new Notification('Pomodoro Timer', {
                            body: message,
                            icon: '/favicon.ico'
                        });
                    }

                    pomodoroState.notificationSent = true;
                    localStorage.setItem('pomodoroTimerState', JSON.stringify(pomodoroState));
                }
            }
        } catch (e) {
            console.error('Pomodoro background check error:', e);
        }
    }
}

export function startBackgroundMonitoring() {
    if (!browser || backgroundCheckInterval !== null) return;

    backgroundCheckInterval = window.setInterval(checkTimersInBackground, 1000);
}

export function stopBackgroundMonitoring() {
    if (backgroundCheckInterval !== null) {
        clearInterval(backgroundCheckInterval);
        backgroundCheckInterval = null;
    }
} 